import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ActiveQuotationForm from "../components/quotation/ActiveQuotationForm";
import { PersistentPageLoading } from "../components/ui/page-structure";
import {
  buildActiveQuotationPayload,
  activeQuotationError,
  createQuotationDraft,
  quotationDetailDraft,
  serviceMemberDraft,
  validateActiveQuotation,
} from "../lib/activeQuotation";
import {
  createQuotation,
  downloadQuotationPdf,
  generateQuotationPdf,
  getQuotation,
  getServiceTicketSqAction,
  listEligibleServiceTickets,
  listQuotationItems,
  updateQuotation,
} from "../lib/quotationApi";
import { getServiceTicket, normalizeServiceTicketResponse } from "../lib/serviceApi";
import { quotationHistoryReturnUrl } from "../lib/quotationNavigation";
import { normalizeActiveQuotationDraft } from "../lib/activeQuotation";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";

function isCanceled(error) {
  return error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
}

export default function QuotationFormPage() {
  const { quotationId, quotationType } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mode = !quotationId ? "create" : location.pathname.endsWith("/edit") ? "edit" : "view";
  const initialType = quotationId ? null : String(quotationType || "").toLowerCase();
  const [draft, setDraft] = useState(() => createQuotationDraft(initialType));
  const [loading, setLoading] = useState(Boolean(quotationId || initialType === "sq"));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [items, setItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateBranch, setCandidateBranch] = useState("jakarta");
  const requestId = useRef(0);
  const generateInFlight = useRef(false);
  const baselineRef = useRef(normalizeActiveQuotationDraft(draft));

  useEffect(() => {
    if (!quotationId) return undefined;
    const controller = new AbortController();
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    setConflict(false);
    getQuotation(quotationId, { signal: controller.signal }).then((detail) => {
      if (id !== requestId.current) return;
      if (mode === "edit" && detail?.permissions?.edit !== true) {
        navigate(`/quotations/${encodeURIComponent(quotationId)}${location.search}`, {
          replace: true,
          state: location.state,
        });
        return;
      }
      const next = quotationDetailDraft(detail);
      baselineRef.current = normalizeActiveQuotationDraft(next);
      setDraft(next);
    }).catch((requestError) => {
      if (id === requestId.current && !isCanceled(requestError)) setError(activeQuotationError(requestError));
    }).finally(() => { if (id === requestId.current) setLoading(false); });
    return () => controller.abort();
  }, [location.search, location.state, mode, navigate, quotationId]);

  useEffect(() => {
    if (quotationId || initialType !== "sq") return undefined;
    const ticketId = searchParams.get("ticket") || "";
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setConflict(false);
    Promise.all([
      getServiceTicketSqAction(ticketId, { signal: controller.signal }),
      getServiceTicket(ticketId),
    ]).then(([action, response]) => {
      if (action?.active_quotation_id) {
        navigate(`/quotations/${encodeURIComponent(action.active_quotation_id)}`, { replace: true });
        return;
      }
      if (!action?.eligible_for_create) throw new Error("This Service Ticket is not currently eligible for SQ creation.");
      const ticket = normalizeServiceTicketResponse(response);
      if (!ticket) throw new Error("Service Ticket response was malformed.");
      const member = serviceMemberDraft({
        service_ticket_id: ticket.id,
        service_ticket_number: ticket.ticket_number,
        branch: ticket.branch,
        stage: ticket.stage,
        serial_number: ticket.device?.serial_number,
        model: ticket.device?.model,
        customer: {
          company_store_name: ticket.owner?.name || "",
          pic: ticket.owner?.pic || "",
          contact: ticket.owner?.contact || "",
          address: ticket.owner?.address || "",
        },
        problem: ticket.problem,
        resolution: ticket.resolution,
        dvt_warranty_status: ticket.warranty?.dvt?.status,
        service_cost: ticket.service_cost,
        updated_at: ticket.updated_at,
      });
      const next = { ...createQuotationDraft("sq"), branch: ticket.branch, service_tickets: [member], customer_source_ticket_id: ticket.id };
      baselineRef.current = normalizeActiveQuotationDraft(next);
      setDraft(next);
    }).catch((requestError) => { if (!isCanceled(requestError)) setError(activeQuotationError(requestError)); }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [initialType, navigate, quotationId, searchParams]);

  useEffect(() => {
    if (draft.type !== "cq") return undefined;
    const controller = new AbortController();
    listQuotationItems({ signal: controller.signal }).then(setItems).catch(() => setItems([]));
    return () => controller.abort();
  }, [draft.type]);

  useEffect(() => {
    const membershipEditable = !quotationId || draft.quotation?.permissions?.membership_edit;
    if (!quotationId || draft.type !== "mq" || mode === "view" || !membershipEditable) return undefined;
    const controller = new AbortController();
    setCandidatesLoading(true);
    listEligibleServiceTickets({
      branch: quotationId ? draft.branch : candidateBranch,
      ...(quotationId ? { quotation_id: quotationId } : {}),
    }, { signal: controller.signal }).then((response) => {
      setCandidates(Array.isArray(response?.data) ? response.data : []);
    }).catch((requestError) => {
      if (!isCanceled(requestError)) setError(activeQuotationError(requestError));
    }).finally(() => setCandidatesLoading(false));
    return () => controller.abort();
  }, [candidateBranch, draft.branch, draft.quotation?.permissions?.membership_edit, draft.type, mode, quotationId]);

  const hydrateCandidate = async (candidate) => {
    const response = await getServiceTicket(candidate.service_ticket_id);
    const ticket = normalizeServiceTicketResponse(response);
    if (!ticket) throw new Error("Service Ticket response was malformed.");
    return serviceMemberDraft({
        ...candidate,
        service_ticket_id: ticket.id,
        service_ticket_number: ticket.ticket_number,
        branch: ticket.branch,
        stage: ticket.stage,
        serial_number: ticket.device?.serial_number,
        model: ticket.device?.model,
        customer: { company_store_name: ticket.owner?.name || "", pic: ticket.owner?.pic || "", contact: ticket.owner?.contact || "", address: ticket.owner?.address || "" },
        problem: ticket.problem,
        resolution: ticket.resolution,
        dvt_warranty_status: ticket.warranty?.dvt?.status,
        service_cost: ticket.service_cost,
        updated_at: ticket.updated_at,
      });
  };

  const addCandidate = async (candidate) => {
    setError("");
    setConflict(false);
    try {
      const member = await hydrateCandidate(candidate);
      setDraft((current) => current.service_tickets.some((row) => row.service_ticket_id === member.service_ticket_id) ? current : { ...current, service_tickets: [...current.service_tickets, member] });
    } catch (requestError) {
      setError(activeQuotationError(requestError));
    }
  };

  const applyCreateMqSelection = async (selected, branch) => {
    setError("");
    setConflict(false);
    const retained = new Map(draft.service_tickets.map((member) => [member.service_ticket_id, member]));
    const newCandidates = selected.filter((candidate) => !retained.has(candidate.service_ticket_id));
    try {
      const hydrated = await Promise.all(newCandidates.map(hydrateCandidate));
      const hydratedById = new Map(hydrated.map((member) => [member.service_ticket_id, member]));
      const members = selected.map((candidate) => retained.get(candidate.service_ticket_id) || hydratedById.get(candidate.service_ticket_id));
      setCandidateBranch(branch);
      setDraft((current) => ({
        ...current,
        branch,
        service_tickets: members,
        customer_source_ticket_id: members.some((member) => member.service_ticket_id === current.customer_source_ticket_id)
          ? current.customer_source_ticket_id
          : "",
      }));
      return true;
    } catch (requestError) {
      setError(activeQuotationError(requestError));
      return false;
    }
  };

  const retainRemovedCandidate = (member) => setCandidates((current) => current.some(
    (candidate) => candidate.service_ticket_id === member.service_ticket_id
  ) ? current : [{
    service_ticket_id: member.service_ticket_id,
    service_ticket_number: member.service_ticket_number,
    branch: member.branch || draft.branch,
    stage: member.stage,
    customer_name: member.customer?.company_store_name || "",
    serial_number: member.serial_number || "",
    updated_at: member.updated_at,
  }, ...current]);

  const save = async () => {
    if (saving) return;
    const validation = validateActiveQuotation(draft, {
      currentItemIds: draft.type === "cq" ? new Set(items.map((item) => item.id)) : null,
    });
    if (validation) { setError(validation); return; }
    setSaving(true);
    setError("");
    setConflict(false);
    try {
      const payload = buildActiveQuotationPayload(draft, draft.quotation);
      const response = draft.quotation
        ? await updateQuotation(quotationId, payload)
        : await createQuotation(payload);
      const saved = response?.data;
      navigate(`/quotations/${encodeURIComponent(saved.id)}${location.search}`, { replace: true, state: location.state });
    } catch (requestError) {
      setConflict(requestError?.response?.status === 409);
      setError(activeQuotationError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (!quotationId || generateInFlight.current) return;
    generateInFlight.current = true;
    setGenerating(true);
    try {
      downloadQuotationPdf(await generateQuotationPdf(quotationId));
    } catch (requestError) {
      toast.error(activeQuotationError(requestError));
    } finally {
      generateInFlight.current = false;
      setGenerating(false);
    }
  };

  const back = () => {
    if (mode === "edit") navigate(`/quotations/${quotationId}${location.search}`, { state: location.state });
    else if (quotationId) navigate(quotationHistoryReturnUrl(location.search));
    else navigate(location.state?.quotationHistoryReturn || (initialType === "sq" ? "/service/sold" : "/quotations/create"));
  };

  const quotationDirty = mode !== "view" && JSON.stringify(normalizeActiveQuotationDraft(draft)) !== JSON.stringify(baselineRef.current);
  const quotationGuard = useUnsavedChangesGuard(quotationDirty && !saving);

  const changeType = () => {
    setDraft(createQuotationDraft(""));
    setCandidateBranch("jakarta");
    setCandidates([]);
    setError("");
    setConflict(false);
    navigate("/quotations/create", { replace: true, state: location.state });
  };

  if (!quotationId && !["sq", "mq", "cq"].includes(initialType)) {
    return <div className="rounded-lg border bg-card p-6 text-sm text-destructive">Unsupported quotation type.</div>;
  }
  if (loading) return <div className="min-w-0 pb-24" data-testid="quotation-form-loading"><PersistentPageLoading label="Loading quotation..." data-testid="quotation-form-loading-header" /></div>;

  return <>
    <ActiveQuotationForm
      mode={mode}
      draft={draft}
      setDraft={setDraft}
      items={items}
      candidates={candidates}
      candidatesLoading={candidatesLoading}
      candidateBranch={candidateBranch}
      setCandidateBranch={(branch) => {
        setCandidateBranch(branch);
        if (!quotationId) setDraft((current) => ({ ...current, service_tickets: [], customer_source_ticket_id: "" }));
      }}
      onAddCandidate={addCandidate}
      onApplyCreateMqSelection={applyCreateMqSelection}
      initialPickerOpen={!quotationId && initialType === "mq"}
      onMemberRemoved={retainRemovedCandidate}
      onMqEmptied={() => navigate("/quotations/create", { state: location.state })}
      onChangeType={changeType}
      onBack={() => quotationGuard.requestLeave(back)}
      onEdit={() => navigate(`/quotations/${quotationId}/edit${location.search}`, { state: location.state })}
      onGenerate={generate}
      onSave={save}
      onReload={() => navigate(0)}
      saving={saving}
      generating={generating}
      error={error}
      conflict={conflict}
    />
    {quotationGuard.dialog}
  </>;
}
